import {
    type PlaylistFormValues,
    type PlaylistLocalePack,
    type PlaylistRecord,
    type PlaylistStreamEntry,
} from './PlaylistEditor.types';

/*
    Form field names match the server keys, so fromForm merges into the loaded record with no
    translation layer. The four locale pack fields are flat on the form, because EntityMetadataForm
    and the pickers read them by name.
*/
export function toForm(record: PlaylistRecord): PlaylistFormValues {
    const localePack = record.locale_pack;
    const entityMetadata = record.entity_metadata;
    const hoursOfCredit = localePack?.hours_of_credit;

    return {
        title: record.title || '',
        tag: record.tag || '',
        description: record.description || '',
        locale: record.locale || 'en',
        content_family_identifier: localePack?.content_family_identifier || '',
        course_code_prefix: localePack?.course_code_prefix || '',
        course_code: localePack?.course_code || '',

        // A string, so a 0 survives the shared TextField's `value || ''`.
        hours_of_credit: hoursOfCredit === null || hoursOfCredit === undefined ? '' : String(hoursOfCredit),
        stream_locale_pack_ids: record.stream_entries.map(entry => ({
            locale_pack_id: entry.locale_pack_id,
        })),
        seo_title: entityMetadata?.title || '',
        seo_description: entityMetadata?.description || '',
        seo_canonical_url: entityMetadata?.canonical_url || '',
        tweet_template: entityMetadata?.tweet_template || '',
    };
}

// Rebuilds stream_entries in the picker's order, reusing each existing entry. An entry can
// carry keys this editor does not read.
function streamEntriesForSave(values: PlaylistFormValues, record: PlaylistRecord): PlaylistStreamEntry[] {
    const existingByLocalePackId = new Map(record.stream_entries.map(entry => [entry.locale_pack_id, entry]));

    return values.stream_locale_pack_ids.map(
        row => existingByLocalePackId.get(row.locale_pack_id) || { locale_pack_id: row.locale_pack_id },
    );
}

/*
    The locale pack the save sends.

    content_items goes back exactly as it arrived. update_locale_pack reconciles the pack's
    membership from that array and detaches every translation it leaves out, so a partial array
    silently unlinks translations and a missing one raises.

    An empty Content Family Identifier or Credit Hours input sends no key at all. Ruby treats '' as
    truthy, so update_locale_pack would assign it, and content_family_identifier is NOT NULL with no
    blankness check. Every blank identifier then forms one family, which merges unrelated
    specializations and changes a student's score. The Angular selectize sent no key when cleared.

    Course code and its prefix still send '', which is what the Angular text inputs did.
*/
function localePackForSave(values: PlaylistFormValues, localePack: PlaylistLocalePack): PlaylistLocalePack {
    const hoursOfCredit = values.hours_of_credit.trim();
    const contentFamilyIdentifier = values.content_family_identifier;

    return {
        ...localePack,
        course_code_prefix: values.course_code_prefix,
        course_code: values.course_code,
        ...(contentFamilyIdentifier === '' ? {} : { content_family_identifier: contentFamilyIdentifier }),
        ...(hoursOfCredit === '' ? {} : { hours_of_credit: Number(hoursOfCredit) }),
    };
}

export function fromForm(values: PlaylistFormValues, record: PlaylistRecord): PlaylistRecord {
    const localePack = record.locale_pack;

    return {
        // Every key the server reads must be present, and the loaded record is the only complete
        // source of them. Spreading it also carries through the fields this form does not edit.
        ...record,
        title: values.title,
        tag: values.tag,
        description: values.description,
        locale: values.locale,
        stream_entries: streamEntriesForSave(values, record),

        // handle_locale_pack runs only when the key is present, so it always is. A non-English
        // playlist sends the pack unchanged, because update_locale_pack skips one.
        locale_pack: localePack && values.locale === 'en' ? localePackForSave(values, localePack) : localePack,
    };
}
